import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ActionList, ActionMenu, FormControl, Label} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import styles from './SpacesOwnerDropdown.module.css'

export interface OwnerItem {
  name: string
  avatarUrl: string
  displayName: string
  id: number
  type: 'User' | 'Organization'
  allowsPrivateReposFromOtherOrgs?: boolean
}

interface SpacesOwnerDropdownProps {
  onSelect: (owner: OwnerItem) => void
  repoId?: number
}

export function SpacesOwnerDropdown({onSelect, repoId}: SpacesOwnerDropdownProps) {
  interface OwnersResult {
    owners: OwnerItem[]
  }

  const [ownerItems, setOwnerItems] = useState<OwnerItem[]>([])
  const [displayLoadingError, setDisplayLoadingError] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<OwnerItem | undefined>(undefined)

  const handleSelectOwner = useCallback(
    (owner: OwnerItem) => {
      setSelectedOwner(owner)
      onSelect(owner)
    },
    [onSelect],
  )

  const handleLoadingError = useCallback(() => {
    setOwnerItems([])
    setDisplayLoadingError(true)
  }, [])

  useEffect(() => {
    const loadOwners = async () => {
      try {
        const url = repoId
          ? `/github-copilot/chat/custom_copilots_owners?repo_id=${repoId}`
          : '/github-copilot/chat/custom_copilots_owners'

        const result = await verifiedFetchJSON(url)
        const data = (await result.json()) as OwnersResult
        const owners = data?.owners
        if (!result.ok || !owners) {
          handleLoadingError()
          return
        }
        setOwnerItems(owners)
        if (owners[0]) {
          handleSelectOwner(owners[0])
        }
      } catch {
        handleLoadingError()
      }
    }

    void loadOwners()
  }, [handleLoadingError, handleSelectOwner, repoId])

  const getCaption = () => {
    const screenReaderCaption = (owner: OwnerItem) => {
      return <span className="sr-only">Space owner. Required. Selected owner: @{owner.displayName}.</span>
    }

    if (!selectedOwner) {
      return (
        <span>
          <span className="sr-only">Space owner. Required.</span> Where we will store your space.
        </span>
      )
    } else if (selectedOwner.type === 'User') {
      return (
        <div className="mt-2 d-flex flex-row flex-shrink flex-items-center">
          {screenReaderCaption(selectedOwner)}
          <Label className="mr-1">Private</Label>
          <span {...testIdProps('owner-user-caption')}>Only you will be able to see this space.</span>
        </div>
      )
    } else {
      return (
        <div className="mt-2 d-flex flex-row flex-shrink">
          {screenReaderCaption(selectedOwner)}
          <Label className={styles.orgLabel}>Private</Label>
          <span {...testIdProps('owner-org-caption')}>
            Only visible to you and organization admins. You can share it later.
          </span>
        </div>
      )
    }
  }

  const headerButton = () => {
    const displayName = selectedOwner?.name || 'Choose an owner'
    const buttonAvatar = selectedOwner ? (
      <GitHubAvatar src={selectedOwner.avatarUrl} key={selectedOwner.avatarUrl} />
    ) : undefined

    return (
      <ActionMenu.Button
        data-testid="owner-dropdown-anchor"
        alignContent="start"
        aria-describedby="owner-caption"
        leadingVisual={buttonAvatar}
      >
        {displayName}
      </ActionMenu.Button>
    )
  }

  return (
    <div className="mt-2 mb-1">
      <FormControl>
        <FormControl.Label required>Owner</FormControl.Label>
        <ActionMenu>
          {headerButton()}
          <ActionMenu.Overlay width="large" maxHeight="large" className={styles.ownerDropdownOverlay}>
            <ActionList>
              <ActionList.Group className={styles.ownerListGroup}>
                <ActionList selectionVariant="single">
                  {ownerItems &&
                    ownerItems.map(item => {
                      const {name, avatarUrl, displayName} = item
                      return (
                        <ActionList.Item
                          key={name}
                          selected={name === selectedOwner?.name}
                          onSelect={() => handleSelectOwner(item)}
                          className={styles.ownerListItem}
                        >
                          <ActionList.LeadingVisual>
                            <GitHubAvatar src={avatarUrl} />
                          </ActionList.LeadingVisual>
                          {name}
                          <ActionList.Description>{displayName}</ActionList.Description>
                        </ActionList.Item>
                      )
                    })}
                  {!ownerItems.length && !displayLoadingError && (
                    <ActionList.Item key="fetching-owners" disabled>
                      Fetching owners…
                    </ActionList.Item>
                  )}
                  {displayLoadingError && (
                    <ActionList.Item key="error-fetching-owners" disabled className={styles.errorListItem}>
                      An error occurred while loading owners. Please reopen the dialog to try again.
                    </ActionList.Item>
                  )}
                </ActionList>
              </ActionList.Group>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>

        <FormControl.Caption id="owner-caption">{getCaption()}</FormControl.Caption>
      </FormControl>
    </div>
  )
}

try{ SpacesOwnerDropdown.displayName ||= 'SpacesOwnerDropdown' } catch {}